import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import React from 'react';
import useRect from 'hooks/common/useRect';
import Chart from 'chart.js/auto';


const config = (hash, onChangeChartInfo) => ({
  type: 'line',
  data: {
    labels: hash.labels,
    datasets: [{
      label: T('혈당지수'),
      data: hash.labels.map(time => hash[time]),
      pointRadius: 0,
      cubicInterpolationMode: 'monotone',
      tension: 0.4,
      borderColor: '#FF7D5E',
      // backgroundColor: '#FF7D5E33',
      // fill: 'start',
    }],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        display: true,
        afterTickToLabelConversion: (scaleInstance) => {
          const ticks = scaleInstance.ticks;
          const newTicks = ticks?.filter((tick) => {
            return tick.label !== '';
          });
          scaleInstance.ticks = newTicks;
        },
        ticks: {
          color: '#000000',
          stepSize: 1,
          callback: (value) => {
            if (/\d\d00$/.test(hash.labels[value])) {
              return hash.labels[value].replace(/00$/, 'H');
            } else {
              return '';
            }
          },
        },
        title: {
          display: false,
        },
        grid: {
          drawTicks: false,
        },
      },
      y: {
        display: true,
        afterTickToLabelConversion: (scaleInstance) => {
          const ticks = scaleInstance.ticks;
          const newTicks = ticks?.filter((tick) => {
            switch (tick.value) {
              case 54:
              case 70:
              case 180:
              case 250:
                return true;
              default: {
                const limit = Math.max(300, hash.value);
                return tick.value === limit;
              }
            }
          });
          scaleInstance.ticks = newTicks;
        },
        ticks: {
          color: '#aaaaaa',
          stepSize: 1,
        },
        title: {
          display: false,
        },
        grid: {
          display: false,
        },
        min: 0,
        max: Math.max(300, hash.value + 1),
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: function (context) {
            const label = (context[0].label || '').split('');
            label.splice(2, 0, ':');
            return label.join('');
          }
        },
      },
    },
  },
  plugins: [{
    id: 'yy',
    afterDatasetsDraw: (chart) => {
      let ctx = chart.ctx;
      const xAxis = chart.scales.x;
      const yAxis = chart.scales.y;
      if (!xAxis) return;
      if (!yAxis) return;

      const left = xAxis.left;
      const right = xAxis.right;

      const y300 = yAxis.getPixelForValue(300);
      const y250 = yAxis.getPixelForValue(250);
      const y180 = yAxis.getPixelForValue(180);
      const y70 = yAxis.getPixelForValue(70);
      const y50 = yAxis.getPixelForValue(50);
      const y0 = yAxis.getPixelForValue(0);

      ctx.save();

      ctx.strokeStyle = '#aaaaaa';
      ctx.lineWidth = 1;

      ctx.beginPath()
      ctx.moveTo(left - 5, y250);
      ctx.lineTo(right, y250);
      ctx.stroke();

      ctx.beginPath()
      ctx.rect(left, y180, right - left, y70 - y180);
      ctx.globalAlpha = 0.1;
      ctx.fillStyle = '#000000';
      ctx.fill();
      // ctx.beginPath()
      // ctx.moveTo(left - 5, y180);
      // ctx.lineTo(right, y180);
      // ctx.stroke();

      // ctx.beginPath()
      // ctx.moveTo(left - 5, y70);
      // ctx.lineTo(right, y70);
      // ctx.stroke();

      ctx.beginPath()
      ctx.moveTo(left - 5, y50);
      ctx.lineTo(right, y50);
      ctx.stroke();

      if (hash.type === 'max') {
        const y = yAxis.getPixelForValue(hash.maxValue);
        onChangeChartInfo && onChangeChartInfo({
          left,
          width: right - left,
          top: y300,
          height: y0 - y300,
          time: hash.maxTime,
          max: y,
        });

      } else if (hash.type === 'min') {
        const y = yAxis.getPixelForValue(hash.minValue);
        onChangeChartInfo && onChangeChartInfo({
          left,
          width: right - left,
          top: y300,
          height: y0 - y300,
          time: hash.minTime,
          min: y,
        });

      }

      ctx.restore();
    },
  }],
});

export default function BloodGlocoseDialogChart(props) {
  const {
    value,
    bldsugr_graph_list = [],
    onChangeChartInfo,
    type,
    stime,
    ...rest
  } = props;

  const hash = React.useMemo(() => {
    const targetTime = stime.toFormat('HHmm');
    const data = {
      labels: [],
      events: [],
    };
    if (!bldsugr_graph_list || !bldsugr_graph_list.length) return data;

    let maxTime = targetTime;
    let maxValue = bldsugr_graph_list[0].bld_sugr_value;

    let minTime = targetTime;
    let minValue = bldsugr_graph_list[0].bld_sugr_value;

    bldsugr_graph_list.forEach((item, index) => {
      data.labels.push(item.bld_sugr_mod_time);
      data[item.bld_sugr_mod_time] = item.bld_sugr_value;
      if (maxTime === item.bld_sugr_time) {
        maxValue = item.bld_sugr_value;
        minValue = item.bld_sugr_value;
      }
    });
    data.maxTime = maxTime;
    data.maxValue = maxValue;
    data.minTime = minTime;
    data.minValue = minValue;
    data.type = type;
    data.value = value;
    return data;
  }, [bldsugr_graph_list, stime, type, value]);

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();

  React.useEffect(() => {
    const chart = new Chart(canvasRef.current, config(hash, onChangeChartInfo));
    chartRef.current = chart;
    return () => {
      chart.destroy();
      delete chart.config;
    };
  }, [hash, onChangeChartInfo]);

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);

  return (
    <div
      {...ex(['Chart', 'BloodGlocoseDialogChart'], rest)}
      {...{ ref }}
    >
      <canvas
        {...{
          ref: canvasRef,
          width: rect.width,
          height: rect.height,
        }}
      />
    </div >
  );
}
